import path from "path";

import type { AxiosError, AxiosRequestTransformer } from "axios";
import axios from "axios";
import moment from "moment";
import { captureException } from "@sentry/react";

import type { RootState } from "../../store/state/rootReducer";

// formatting the URL to query the backend
export const getURL = (): string => {
  const { location: { hostname = "" } = {} } = window;

  if (hostname === "theirstory-react.ngrok.io") {
    return "https://theirstory-node.ngrok.io";
  }
  if (hostname === "ts-react.ngrok.io") {
    return "https://ts-node.ngrok.io";
  }

  // staging
  if (hostname === "ts-frontend-staging.herokuapp.com") {
    return "https://ts-backend-staging.herokuapp.com";
  }

  // staging
  if (hostname === "staging.theirstory.io") {
    return "https://stagingnode.theirstory.io";
  }

  // production
  if (hostname === "ts-frontend-alpha.herokuapp.com") {
    return "https://ts-backend-alpha.herokuapp.com";
  }

  // production
  if (
    hostname === "alpha.theirstory.io" ||
    hostname === "theirstory.io" ||
    hostname === "www.theirstory.io" ||
    hostname === "theirstory.netlify.app"
  ) {
    return "https://node.theirstory.io";
  }

  // provided api url
  if (process.env.API_BASE_URL && process.env.API_BASE_URL.length > 0) {
    return process.env.API_BASE_URL;
  }

  // local
  // noinspection HttpUrlsUsage
  return `http://${window.location.hostname}:8080`;
};

export function forceDownloadOfData(contentType, data, filename = ""): void {
  forceDownloadOfUrl(`data:${contentType};charset=utf-8,${encodeURIComponent(data)}`, filename);
}

export function forceDownloadOfUrl(url: string, filename = ""): void {
  const el = document.createElement("a");
  el.setAttribute("href", url);
  el.setAttribute("download", filename);
  el.style.display = "none";
  document.body.appendChild(el);
  el.click();
  document.body.removeChild(el);
}

export async function trackVisitor(isLandingPage = false) {
  const visitorId = localStorage.getItem("ts_visitor_id") || uuidv4();
  if (!localStorage.getItem("ts_visitor_id")) {
    localStorage.setItem("ts_visitor_id", visitorId);
  }
  try {
    const lastTimestamp = localStorage.getItem("ts_last_timestamp");
    const currentTime = new Date().getTime();
    const {
      userAgent,
      platform: userPlatform,
      connection: userConnection,
    } = window.navigator ? window.navigator : ({} as any);
    const {
      downlink: networkDown,
      rtt: networkRtt,
      effectiveType: networkEffectiveType,
    } = userConnection && userConnection.rtt ? userConnection : ({} as any);
    const landingPage = window && window.location && window.location.pathname ? window.location.pathname : "/";
    // eslint-disable-next-line global-require
    const FingerprintJS = await require("@fingerprintjs/fingerprintjs");
    // get browser fingerprint visitor id
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const fingerprintId = result.visitorId;
    const googleIDs = await getGoogleIDs();
    // record a new visit if no previous visit or at least four hours since last timestamp
    if (isLandingPage && (!lastTimestamp || currentTime - parseInt(lastTimestamp, 10) > 60000 * 60 * 4)) {
      await axios.post("/visits/create", {
        visitor_id: visitorId,
        fingerprint_id: fingerprintId,
        landing_page: landingPage,
        user_agent: userAgent,
        user_platform: userPlatform,
        network_down: networkDown,
        network_rtt: networkRtt,
        network_effective_type: networkEffectiveType,
        ...googleIDs,
      });

      localStorage.setItem("ts_last_timestamp", currentTime as any);
    } else if (localStorage.getItem("ts_visitor_id")) {
      localStorage.setItem("ts_last_timestamp", currentTime as any);
    }
  } catch (e) {
    // We don't want this function to throw exceptions.
    // Even if there's an error reporting the visitor ID,
    // we want to still try and render the actual application
    console.error("Error initializing visitor id: ", e);
    captureException(e);
  }
  return visitorId;
}

export function uuidv4(): string {
  // from https://stackoverflow.com/a/2117523
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    /* eslint-disable no-bitwise */
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    /* eslint-enable no-bitwise */
    return v.toString(16);
  });
}

export async function getGoogleIDs() {
  // retrieve GA4 values set with Gtag API Get tag in GTM
  // note that due to tag firing order this may not be available
  // upon initial load
  try {
    const tracker = (window as any).google_tag_manager["G-QP8TC71F3L"].dataLayer.get("gtagApiResult");
    return {
      ga_client_id: tracker.client_id,
      ga_session_id: tracker.session_id,
      gclid: tracker.gclid,
    };
  } catch (e) {
    return {};
  }
}

export function pushGtm(event) {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push(event);
}

export function isAxiosError(err: Error): err is AxiosError {
  return (err as AxiosError).response !== undefined && (err as AxiosError).config !== undefined;
}

interface FileNameForStoryOpts {
  ext?: string;
}

// TODO: A great candidate for the common lib!
export function buildFileNameForStory(story, opts: FileNameForStoryOpts = {}): string {
  const ext = opts.ext
    ? `${opts.ext.startsWith(".") ? "" : "."}${opts.ext}`
    : story.custom_archive_url
    ? path.extname(story.custom_archive_url)
    : ".mp4";
  const name: string = story.title ? story.title.replace(/[^a-zA-Z0-9]+/g, "_") : "Untitled";

  // It would be nice if we could generate timestamp in tz of the user but because this
  // function also generates filenames on the server where presently we only know TZ of the
  // server, let's stick to UTC for now. Not ideal but at least predictable
  const timestamp = moment(story.recording_started_at || story.created_at)
    .utc()
    .format("YYYY-MM-DD_HH-mm-ss");

  return `${name}_${timestamp}${ext}`;
}

export function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// unique identifier for every load (help us track if same user has
// more than one tab open)
const instanceId = uuidv4();

export function buildLoggerTags(state: RootState, storyId?: string) {
  const { auth } = state;
  return {
    userId: auth.user_id ? auth.user_id : "Anonymous",
    user: auth.full_name,
    visitorId: auth.visitorId,
    instanceId,
    ...(storyId != null ? { storyId } : {}),
  };
}

export const nukeAuthHeaderAxiosReqTransform: AxiosRequestTransformer = (data, headers) => {
  // Axios 1.x uses AxiosHeaders objects which have a .delete() method
  if (headers && typeof (headers as any).delete === "function") {
    (headers as any).delete("authorization");
  } else if (headers && (headers as any).common) {
    delete (headers as any).common.authorization;
  }
  return data;
};

export const getInitials = (name) => {
  const words = name.split(" ");
  const initialsArray = words.map((word) => word.charAt(0).toUpperCase());
  const initials = initialsArray.join("").slice(0, 3);
  return initials;
};
